import "../../column-checkmark.ts";

import { CheckmarkCell } from "../../column-checkmark.ts";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { type Option } from "@tento-lona/sheets";

/** A home card that toggles a checkmark row's value for a day. */
@component({ name: "std-checkmark-card" })
export class SheetRowCheckmarkCard extends LonaWebComponent {
  #$cell: CheckmarkCell = this.hydrate("cell");
  #isChecked: boolean = false;
  #onToggle: Option<(checked: boolean) => void> = null;

  static makeWith(props: SheetRowCheckmarkCard.Props): SheetRowCheckmarkCard {
    return SheetRowCheckmarkCard.make().bind(props);
  }

  bind(props: SheetRowCheckmarkCard.Props): this {
    this.#onToggle = props.onToggle ?? null;

    // Set up accessibility
    this.#$cell.setAttribute("role", "checkbox");
    this.#$cell.setAttribute("tabindex", "0");
    this.#$cell.setAttribute("aria-checked", "false");
    this.#$cell.setAttribute("aria-label", "Toggle checkmark");

    GESTURE_MANAGER.addPointerEvent(this.#$cell, {
      onClick: () => {
        this.#toggle();
      },
    });

    this.#$cell.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        this.#toggle();
      }
    });

    return this;
  }

  #toggle(): void {
    this.#isChecked = !this.#isChecked;
    this.#updateVisual();
    this.#onToggle?.(this.#isChecked);
  }

  setChecked(checked: boolean): void {
    this.#isChecked = checked;
    this.#updateVisual();
  }

  #updateVisual(): void {
    this.#$cell.bind(this.#isChecked ? "var(--red)" : null, "current-or-future");
    this.#$cell.setAttribute("aria-checked", String(this.#isChecked));
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
        height: 100%;
      }
      .container {
        position: relative;
        padding: 12px;
        height: 100%;
        justify-content: center;
        align-items: center;
        padding-bottom: 60px;
      }
      #cell {
        width: 80px;
        height: 80px;
        border-radius: 16px;
        cursor: pointer;
      }

      #cell:focus-visible {
        outline: 2px solid var(--blue);
        outline-offset: 4px;
      }
    `,
  ];

  static $html: HTMLTemplateElement = html`
    <std-col class="container">
      <std-checkmark-cell id="cell"></std-checkmark-cell>
    </std-col>
  `;
}

export namespace SheetRowCheckmarkCard {
  export type Props = {
    onToggle?: (checked: boolean) => void;
  };
}
